import type { TLRecord } from '@digitalsamba/tldraw';
import { all, call } from 'redux-saga/effects';
import * as Sentry from '@sentry/react';
import { logger } from 'utils/logger';
import { DRAFTS_LOG, DRAFTS_TAG } from 'utils/whiteboard/drafts/draftsLog';
import { reseatRecordSaga } from 'features/whiteboard/drafts/sagas/reseatRecordSaga';

type ImageShapeLike = TLRecord & { props?: { assetId?: string } };

export function* reseatRecordsSaga(
  records: TLRecord[],
  encryptionKey: CryptoKey | undefined,
  whiteboardId: string
) {
  const reseated: (TLRecord | null)[] = yield all(
    records.map((record) => call(reseatRecordSaga, record, encryptionKey))
  );

  const droppedAssets = new Set(
    records.filter((_, index) => reseated[index] === null).map((record) => String(record.id))
  );

  // One report for the batch: a refusal is normally systemic, and a board of twenty images would
  // otherwise open twenty issues for one cause.
  if (droppedAssets.size) {
    const context = { whiteboardId, dropped: droppedAssets.size, records: records.length };

    logger
      .remote(DRAFTS_LOG)
      .error('[drafts] Restore is dropping images it could not bring back', context);

    yield call(
      Sentry.captureException,
      new Error(`Whiteboard restore dropped ${droppedAssets.size} image(s)`),
      { tags: { feature: DRAFTS_TAG, operation: 'reseatImages' }, extra: context }
    );
  }

  return reseated
    .filter((record): record is TLRecord => record !== null)
    .filter((record) => {
      const { assetId } = (record as ImageShapeLike).props ?? {};

      return !assetId || !droppedAssets.has(assetId);
    });
}
